import { shallowEqual, useSelector } from 'react-redux';
import Store from '../Store';
import {
   selectFilteredRecentChatData,
   selectHasMore,
   selectLoading,
   selectSearchTextByScope,
} from './recentChannelSlice';

export const getActiveChannelId = () => Store.getState().channelNavData.activeConversationChannel?.channelId;
export const getAppOnlineStatus = () => Store.getState()?.appOnlineStatus?.isOnline;
export const getSelectedChannels = () => Store.getState()?.channelSelectionData.selectedChannels;
export const getSelectedMessages = channelId => {
   const messages = Store.getState()?.channelConversationData.channelConversations[channelId]?.messages || [];
   const selectedMessages = messages.filter(message => message.isSelected);
   return selectedMessages;
};
export const getMessageById = (channelId, messageId) => {
   const messages = Store.getState()?.channelConversationData.channelConversations[channelId]?.messages || [];
   return messages.find(message => message.messageId === messageId) || null;
};
export const getThumbImage = fileId => {
   if (fileId) return null;

   const state = Store.getState();
   const thumbData = state.mediaImageThumbData?.[fileId];

   return thumbData?.thumbImage || null;
};
export const getRecentChannelList = size => {
   const list = Store.getState()?.recentChannelListData?.recentChannels || [];
   return size ? list.slice(0, size) : list;
};
export const getChannelName = channelId => Store.getState().channelInfoData.channelInfo[channelId]?.name;
export const getChannelInfo = channelId => Store.getState().channelInfoData.channelInfo[channelId];
export const getUserRole = channelId => {
   const channelInfo = Store.getState()?.channelInfoData.channelInfo?.[channelId];
   const member = channelInfo?.member;
   return member?.status === 0 ? member.status : member?.roleId ?? null;
};
export const getChannelStatus = channelId => Store.getState().channelInfoData.channelInfo[channelId]?.status;

export const getMessageMode = channelId =>
   Store.getState().channelInfoData.channelInfo?.[channelId]?.settings?.messageMode;

export const getUserName = userId => Store.getState().userInfoData.users[userId]?.nickName || userId;
export const getConversationChannelData = channelId =>
   Store.getState().channelConversationData?.channelConversations?.[channelId];
export const getChannelMembersCount = channelId => () =>
   Store.getState().channelMembersData.membersByChannel[channelId]?.totalActive ?? 0;
export const getVirtualizedListRef = channelId => () =>
   Store.getState().draftData.virtualizedListRefs?.[channelId] ?? null;
export const getRecordingStatus = () => Store.getState().draftData.data.isRecording ?? null;
export const getChannelUnreadCount = channelId => () =>
   Store.getState().channelInfoData.channelInfo[channelId]?.unreadCount;
export const getChannelLastReadMessageId = channelId => () =>
   Store.getState().channelInfoData.channelInfo[channelId]?.lastReadMessage?.messageId;

export const useChannelNav = () => useSelector(state => state.channelNavData.activeConversationChannel);
export const useLeftPaneData = () => useSelector(state => state.leftPaneNavData);
export const useLeftPaneNav = () => useSelector(state => state.leftPaneNavData.activeLeftPane);
export const useRecentChannelData = scopeKey => {
   const searchText = useSelector(selectSearchTextByScope(scopeKey));

   const recentChannels = useSelector(selectFilteredRecentChatData(scopeKey));
   const loading = useSelector(selectLoading);
   const hasMore = useSelector(selectHasMore);

   return { recentChannels, loading, hasMore, searchText };
};
export const useRecentChannelSearchText = scopeKey => useSelector(selectSearchTextByScope(scopeKey));
export const useChannelMembersCount = channelId => useSelector(getChannelMembersCount(channelId));

export const useIsChannelSelected = channelId =>
   useSelector(state => !!state.channelSelectionData.selectedChannels?.[channelId]);
export const useSelectedChannels = () => useSelector(state => state.channelSelectionData.selectedChannels);
export const useRecentChannelList = size => {
   const list = useSelector(state => state.recentChannelListData.recentChannels);
   return size ? list.slice(0, size) : list;
};
export const useChannelInvitationCount = () => useSelector(state => state.invitations.totalRecords);
export const useConversationChannelData = channelId =>
   useSelector(state => state.channelConversationData.channelConversations[channelId] || {});
export const useChannelInfoData = () => useSelector(state => state.channelInfoData);
export const useCreateChannelData = () => useSelector(state => state.channelInfoData.createChannel);
export const useChannelImageUri = channelId =>
   useSelector(state => state.channelInfoData.channelInfo[channelId]?.imageUri);
export const useChannelIsMember = channelId =>
   useSelector(state => state.channelInfoData.channelInfo[channelId]?.isMember);
export const useChannelName = channelId => useSelector(state => state.channelInfoData.channelInfo[channelId]?.name);
export const useChannelDescription = channelId =>
   useSelector(state => state.channelInfoData.channelInfo[channelId]?.description);
export const useChannelCategory = channelId =>
   useSelector(state => state.channelInfoData.channelInfo[channelId]);
export const useChannelCreated = channelId =>
   useSelector(state => state.channelInfoData.channelInfo[channelId]?.createdAt);
export const useChannelIsMuted = channelId =>
   useSelector(state => state.channelInfoData.channelInfo[channelId]?.isMuted);
export const useChannelIsPinned = channelId =>
   useSelector(state => {
      const pinnedAt = state.channelInfoData.channelInfo[channelId]?.pinnedAt;
      return pinnedAt && pinnedAt > 0 ? pinnedAt : 0;
   });
export const useChannelSettings = channelId =>
   useSelector(state => state.channelInfoData.channelInfo[channelId]?.settings);
export const useChannelPendingStatus = channelId =>
   useSelector(state => state.channelInfoData.channelInfo[channelId]?.pendingActivity);
export const useChannelStatus = channelId => useSelector(state => state.channelInfoData.channelInfo[channelId]?.status);
export const useParticipantsSelected = scopeKey => {
   const participants = useSelector(state => state.channelInfoData.userListSelection?.[scopeKey] || []);
   return participants.length > 0;
};
export const useMembersCount = channelId => {
   return useSelector(state => state.channelInfoData.channelInfo[channelId]?.memberCount);
};
export const useMessageSelectionCount = channelId => {
   return useSelector(state => {
      const messages = state.channelConversationData.channelConversations[channelId]?.messages || [];
      const selectionType = state.channelConversationData.channelConversations[channelId]?.selectionType || '';
      const selectedCount = messages.filter(message => message.isSelected).length;
      return { selectionCount: selectedCount, selectionType };
   });
};
export const useIsAnyMessageSelected = channelId => {
   return useSelector(state => {
      const messages = state.channelConversationData.channelConversations[channelId]?.messages || [];
      return messages.some(message => message.isSelected);
   });
};

export const useChanneInfoActionPending = () => useSelector(state => state.channelInfoData.isActionPending);
export const useAlertModalContent = () => useSelector(state => state.alertModalData.alertModalContent);
export const useActionModalContent = () => useSelector(state => state.actionModalData.actionModalContent);
export const useUserProfile = userId => useSelector(state => state.userInfoData.users[userId]);
export const useUserName = userId => useSelector(state => state.userInfoData.users[userId]?.nickName || userId);
export const useUserDeletedStatus = userId => useSelector(state => state.userInfoData.users[userId]?.isDeletedUser);

export const useUserStatus = userId =>
   useSelector(state => state.userInfoData.users[userId]?.status || "I'm using EN Connect");
export const useUserImage = userId => useSelector(state => state.userInfoData.users[userId]?.image);
export const useMessageMode = channelId =>
   useSelector(state => state.channelInfoData.channelInfo?.[channelId]?.settings?.messageMode);
export const useChannelInfo = channelId => useSelector(state => state.channelInfoData.channelInfo?.[channelId]);
export const useRecentMessage = channelId =>  useSelector(state => state.channelInfoData.channelInfo?.[channelId]?.lastRecentMessage);
export const useUserRole = channelId => {
   return useSelector(state => {
      const channelInfo = state.channelInfoData.channelInfo?.[channelId];
      const member = channelInfo?.member;
      return member?.status === 0 ? member.status : member?.roleId ?? null;
   });
};

export const useMembersList = (channelId, membersCount) => {
   return useSelector(state => {
      const channel = state.channelInfoData.channelInfo?.[channelId];
      const combinedMap = new Map();
      [...(channel.members || []), ...(channel.participants || [])]
         .filter(m => m.status !== 0)
         .forEach(m => {
            combinedMap.set(m.userId, { ...combinedMap.get(m.userId), ...m });
         });

      const allMembers = Array.from(combinedMap.values());

      // If membersCount is provided (i.e., truthy), return only first `membersCount` users
      return membersCount ? allMembers.slice(0, membersCount) : allMembers;
   });
};
export const useTextMessage = userId => useSelector(state => state.draftData.data[userId]?.text);
export const useAppOnlineStatus = () => useSelector(state => state.appOnlineStatus?.isOnline);
export const useMediaStateProgress = msgId => useSelector(state => state.progressData?.[msgId]);
export const useMediaMessages = (channelId, mediaTypeArr = []) => {
   return useSelector(state => {
      const messages = state.channelConversationData.channelConversations[channelId]?.messages || [];
      return messages.filter(
         ({ content: { media } = {}, messageType, isDeletedForMe, isDeletedForEveryone }) =>
            mediaTypeArr.includes(messageType) && !isDeletedForMe && !isDeletedForEveryone && media?.is_uploading === 2,
      );
   }, shallowEqual);
};
export const useRecordingStatus = () => useSelector(state => state.draftData.data.isRecording ?? null);
export const useChannelUnreadCount = channelId => useSelector(getChannelUnreadCount(channelId));
export const useChannelStructuredMessages = channelId =>
   useSelector(state => state.channelConversationData.channelConversations[channelId]?.messages || []);

export const getChannelSearchData = channelId => state =>
   state.channelConversationData.channelSearch[channelId] || {
      searchText: '',
      searchResults: [],
      currentSearchIndex: -1,
      isSearching: false,
   };

export const getChannelSearchText = channelId =>
   Store.getState().channelConversationData.channelSearch[channelId]?.searchText;

export const useChannelSearchData = channelId => useSelector(getChannelSearchData(channelId));
export const useChannelSearchText = channelId => useSelector(getChannelSearchData(channelId)).searchText;
export const useChannelSearchResults = channelId => useSelector(getChannelSearchData(channelId)).searchResults;
export const useChannelSearchIndex = channelId => useSelector(getChannelSearchData(channelId)).currentSearchIndex;
export const useChannelIsSearching = channelId => useSelector(getChannelSearchData(channelId)).isSearching;

export const getChannelMessagesUi = channelId => state =>
   state.channelConversationData.channelUiState?.[channelId] || {};
export const useChannelMessagesUi = channelId => useSelector(getChannelMessagesUi(channelId));
export const useUnreadChannel = () => useSelector(state => state.recentChannelListData.unreadRecords);
export const useLastMessageObject = channelId =>
   useSelector(state => {
      const messages = state.channelConversationData.channelConversations[channelId]?.messages || [];
      // Filter out non-message items (date-label, unread-separator) and get the first actual message
      // Messages are ordered with newest first (index 0)
      const lastMessage = messages.find(msg => msg.type === 'message');
      return lastMessage || null;
   });